import { useState, useEffect, useMemo, useCallback } from 'react'
import { exclusionKeywordsService } from '../../services/exclusionKeywordsService'
import { Field, Input, PageHeader, SpinnerIcon } from '../../components/cms/shared'

const TYPE_LABELS = {
    general: 'Always penalise',
    ict: 'ICT — only when not care-related',
}

const EMPTY_FORM = { keyword: '', category: '', type: 'general' }

export default function ExclusionKeywordsCmsPage() {
    const [items, setItems] = useState([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [search, setSearch] = useState('')
    const [typeFilter, setTypeFilter] = useState('all')
    const [form, setForm] = useState(EMPTY_FORM)
    const [editingId, setEditingId] = useState(null)
    const [busy, setBusy] = useState(false)
    const [rescore, setRescore] = useState({ loading: false, applying: false, result: null, applied: false, error: null })

    const load = useCallback(async () => {
        try {
            const res = await exclusionKeywordsService.getAll()
            setItems(res.data || [])
            setError(null)
        } catch (err) {
            setError(err.message || 'Failed to load exclusion keywords')
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => { load() }, [load])

    const categories = useMemo(() => Array.from(new Set(items.map((i) => i.category))).sort(), [items])

    const visible = useMemo(() => {
        const q = search.trim().toLowerCase()
        return items.filter((i) =>
            (typeFilter === 'all' || i.type === typeFilter) &&
            (!q || i.keyword.toLowerCase().includes(q) || i.category.toLowerCase().includes(q))
        )
    }, [items, search, typeFilter])

    const grouped = useMemo(() => {
        const map = new Map()
        visible.forEach((i) => {
            const key = `${i.type === 'ict' ? 'ICT' : 'General'} · ${i.category}`
            if (!map.has(key)) map.set(key, [])
            map.get(key).push(i)
        })
        return Array.from(map.entries())
    }, [visible])

    const run = async (fn) => {
        setBusy(true)
        setError(null)
        try {
            await fn()
            await load()
        } catch (err) {
            setError(err.message || 'Request failed')
        } finally {
            setBusy(false)
        }
    }

    const submit = (e) => {
        e.preventDefault()
        if (!form.keyword.trim()) return
        run(async () => {
            if (editingId) await exclusionKeywordsService.update(editingId, form)
            else await exclusionKeywordsService.create(form)
            setForm(EMPTY_FORM)
            setEditingId(null)
        })
    }

    const startEdit = (item) => {
        setEditingId(item.id)
        setForm({ keyword: item.keyword, category: item.category, type: item.type })
        window.scrollTo({ top: 0, behavior: 'smooth' })
    }

    const cancelEdit = () => { setEditingId(null); setForm(EMPTY_FORM) }

    const remove = (item) => {
        if (!window.confirm(`Delete exclusion keyword "${item.keyword}"?`)) return
        run(() => exclusionKeywordsService.remove(item.id))
    }

    const previewRescore = async () => {
        setRescore({ loading: true, applying: false, result: null, applied: false, error: null })
        try {
            const res = await exclusionKeywordsService.previewRescore()
            setRescore({ loading: false, applying: false, result: res.data, applied: false, error: null })
        } catch (err) {
            setRescore({ loading: false, applying: false, result: null, applied: false, error: err.message || 'Preview failed' })
        }
    }

    const applyRescore = async () => {
        const r = rescore.result
        if (!r) return
        if (!window.confirm(`Apply the re-score? ${r.newlyExcluded} tender(s) will be hidden from customers and ${r.restored} restored. Nothing is deleted and you can re-run this at any time.`)) return
        setRescore((s) => ({ ...s, applying: true, error: null }))
        try {
            const res = await exclusionKeywordsService.applyRescore()
            setRescore({ loading: false, applying: false, result: res.data, applied: true, error: null })
        } catch (err) {
            setRescore((s) => ({ ...s, applying: false, error: err.message || 'Re-score failed' }))
        }
    }

    if (loading) return <div className="flex items-center justify-center py-24"><SpinnerIcon className="w-6 h-6 animate-spin text-brand-navy" /></div>

    return (
        <div className="space-y-6">
            <PageHeader
                title="Exclusion Keywords"
                description="Tenders mentioning these terms are down-ranked (−20) during ingestion scoring. Changes apply to newly scored tenders immediately. To apply a material change to tenders already held, use Re-score existing tenders below."
            />

            {error && <div className="px-4 py-2 text-sm text-status-error bg-red-50 border border-red-200 rounded-md">{error}</div>}

            <div className="bg-white border border-admin-border rounded-lg shadow-sm p-6 space-y-4">
                <div>
                    <h3 className="text-sm font-bold text-admin-heading">Re-score existing tenders</h3>
                    <p className="text-xs text-admin-muted mt-1 max-w-3xl">
                        Checks every tender already held against the current exclusion keywords. Tenders that the exclusions would now keep out are hidden from customer lists (nothing is deleted, saved tenders are unaffected), and tenders hidden by an earlier run that no longer qualify are restored. Preview first; nothing changes until you apply.
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                    <button type="button" onClick={previewRescore} disabled={rescore.loading || rescore.applying}
                        className="px-4 py-1.5 text-xs font-bold text-brand-navy border border-brand-navy/30 rounded hover:bg-slate-50 disabled:opacity-50">
                        {rescore.loading ? 'Checking…' : 'Preview impact'}
                    </button>
                    <button type="button" onClick={applyRescore}
                        disabled={!rescore.result || rescore.applied || rescore.applying || (rescore.result.newlyExcluded === 0 && rescore.result.restored === 0)}
                        className="px-4 py-1.5 text-xs font-bold bg-brand-navy text-white rounded hover:bg-brand-navy-dark disabled:opacity-50">
                        {rescore.applying ? 'Applying…' : 'Apply to existing tenders'}
                    </button>
                </div>
                {rescore.error && <p className="text-xs text-status-error">{rescore.error}</p>}
                {rescore.result && (
                    <div className="space-y-3 text-sm">
                        <p className="text-admin-text">
                            {rescore.applied ? <strong className="text-green-700">Applied. </strong> : <strong>Preview (nothing changed yet). </strong>}
                            Checked <strong>{rescore.result.scanned}</strong> tenders:{' '}
                            <strong>{rescore.result.newlyExcluded}</strong> {rescore.applied ? 'hidden' : 'would be hidden'},{' '}
                            <strong>{rescore.result.restored}</strong> {rescore.applied ? 'restored' : 'would be restored'},{' '}
                            {rescore.result.unchanged} unchanged. {rescore.result.nowExcluded} in total are excluded by the current rules.
                        </p>
                        {rescore.result.newlyExcludedSample.length > 0 && (
                            <div>
                                <p className="text-xs font-bold text-admin-heading mb-1">{rescore.applied ? 'Hidden' : 'Would be hidden'} (first {rescore.result.newlyExcludedSample.length})</p>
                                <ul className="text-xs text-admin-text divide-y divide-admin-border border border-admin-border rounded">
                                    {rescore.result.newlyExcludedSample.map((t) => (
                                        <li key={t.id} className="px-3 py-1.5"><span className="font-medium">{t.title}</span>{t.buyer ? <span className="text-admin-muted"> — {t.buyer}</span> : null}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        {rescore.result.restoredSample.length > 0 && (
                            <div>
                                <p className="text-xs font-bold text-admin-heading mb-1">{rescore.applied ? 'Restored' : 'Would be restored'} (first {rescore.result.restoredSample.length})</p>
                                <ul className="text-xs text-admin-text divide-y divide-admin-border border border-admin-border rounded">
                                    {rescore.result.restoredSample.map((t) => (
                                        <li key={t.id} className="px-3 py-1.5"><span className="font-medium">{t.title}</span>{t.buyer ? <span className="text-admin-muted"> — {t.buyer}</span> : null}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                )}
            </div>

            <form onSubmit={submit} className="bg-white border border-admin-border rounded-lg shadow-sm p-6 space-y-4">
                <h3 className="text-sm font-bold text-admin-heading">{editingId ? 'Edit keyword' : 'Add keyword'}</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Field label="Keyword / phrase">
                        <Input value={form.keyword} onChange={(v) => setForm((f) => ({ ...f, keyword: v }))} placeholder="e.g. road maintenance" />
                    </Field>
                    <Field label="Category">
                        <input
                            list="exclusion-categories"
                            value={form.category}
                            onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                            placeholder="e.g. Civil / Infrastructure"
                            className="w-full px-3 py-2 text-sm bg-white border border-admin-border rounded-md text-admin-text placeholder-admin-muted focus:outline-none focus:ring-2 focus:ring-brand-navy/20 focus:border-brand-navy/40"
                        />
                        <datalist id="exclusion-categories">
                            {categories.map((c) => <option key={c} value={c} />)}
                        </datalist>
                    </Field>
                    <Field label="Behaviour">
                        <select
                            value={form.type}
                            onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}
                            className="w-full px-3 py-2 text-sm bg-white border border-admin-border rounded-md text-admin-text focus:outline-none focus:ring-2 focus:ring-brand-navy/20"
                        >
                            {Object.entries(TYPE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                        </select>
                    </Field>
                </div>
                <div className="flex items-center gap-3">
                    <button type="submit" disabled={busy || !form.keyword.trim()} className="px-4 py-1.5 text-xs font-bold bg-brand-navy text-white rounded hover:bg-brand-navy-dark transition-all disabled:opacity-50">
                        {busy ? 'Saving…' : editingId ? 'Update' : 'Add'}
                    </button>
                    {editingId && <button type="button" onClick={cancelEdit} className="px-4 py-1.5 text-xs font-bold text-admin-muted border border-admin-border rounded hover:bg-slate-100">Cancel</button>}
                </div>
            </form>

            <div className="flex flex-col md:flex-row gap-3">
                <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search keywords or categories…"
                    className="flex-1 px-3 py-2 text-sm bg-white border border-admin-border rounded-md focus:outline-none focus:ring-2 focus:ring-brand-navy/20"
                />
                <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="px-3 py-2 text-sm bg-white border border-admin-border rounded-md">
                    <option value="all">All types</option>
                    {Object.entries(TYPE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
            </div>

            <p className="text-xs text-admin-muted">{visible.length} of {items.length} keywords</p>

            {grouped.map(([group, list]) => (
                <div key={group} className="bg-white border border-admin-border rounded-lg shadow-sm overflow-hidden">
                    <div className="px-6 py-3 border-b border-admin-border bg-slate-50 text-sm font-bold text-admin-heading">
                        {group} <span className="text-xs font-normal text-admin-muted">({list.length})</span>
                    </div>
                    <ul className="divide-y divide-admin-border">
                        {list.map((item) => (
                            <li key={item.id} className="px-6 py-2.5 flex items-center justify-between gap-4">
                                <span className="text-sm text-admin-text">{item.keyword}</span>
                                <span className="flex items-center gap-3 flex-shrink-0">
                                    <button onClick={() => startEdit(item)} disabled={busy} className="text-[11px] font-bold text-brand-navy hover:underline">Edit</button>
                                    <button onClick={() => remove(item)} disabled={busy} className="text-[11px] font-bold text-red-400 hover:text-red-600">Delete</button>
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>
            ))}

            {grouped.length === 0 && <p className="text-sm text-admin-muted text-center py-8">No exclusion keywords found.</p>}
        </div>
    )
}
